/**
 * Interfaces
 */
import { IUserBaseDto } from './interfaces/user-base';

/**
 * Base class for user models that provides common user properties.
 * 
 * This abstract class handles the basic user information that all user types
 * share, like name, email, and role. It converts between the API's snake_case
 * format and the application's camelCase format automatically.
 * 
 * Other user models extend this class to add their specific properties.
 */
export abstract class UserBaseModel<T extends IUserBaseDto = IUserBaseDto> {

    /**
     * Sets the user's first name.
     * 
     * @param value - The first name as a string
     * @throws Error if the value is not a string
     */
    public set firstName(value: string) {
        if (typeof value !== 'string') {
            throw new Error('First name must be a string');
        }
        this._user.first_name = value;
    }

    /**
     * Gets the user's first name.
     * 
     * @returns The first name
     */
    public get firstName(): string {
        return this._user.first_name;
    }    
    
    /**
     * Sets the user's last name.
     * 
     * @param value - The last name as a string
     * @throws Error if the value is not a string
     */
    public set lastName(value: string) {
        if (typeof value !== 'string') {
            throw new Error('Last name must be a string');
        }
        this._user.last_name = value;
    }

    /**
     * Gets the user's last name.
     * 
     * @returns The last name
     */
    public get lastName(): string {
        return this._user.last_name;
    }

    /**
     * Sets the user's email address.
     * 
     * @param value - The email address as a string
     * @throws Error if the value is not a string
     */
    public set email(value: string) {
        if (typeof value !== 'string') {
            throw new Error('Email must be a string');
        }
        this._user.email = value;
    }

    /**
     * Gets the user's email address.
     * 
     * @returns The email address
     */
    public get email(): string {
        return this._user.email;
    }

    /**
     * Sets the user's role identifier.
     * 
     * The role ID is used to determine what permissions the user has
     * in the system. This is different from the role name which is
     * just a display label.
     * 
     * @param value - The role ID as a string
     * @throws Error if the value is not a string
     */
    public set roleId(value: string) {
        if (typeof value !== 'string') {
            throw new Error('Role ID must be a string');
        }
        this._user.role_id = value;
    }

    /**
     * Gets the user's role identifier.
     * 
     * @returns The role ID
     */
    public get roleId(): string {
        return this._user.role_id;
    }

    /**
     * Creates a new base user model instance.
     * 
     * @param _user - The base user data transfer object from the API
     */
    constructor(private _user: T) { }

    /**
     * Updates the user information with new data from the API.
     * 
     * This method replaces the current user data with the provided data.
     * It's useful when you need to refresh the user information after
     * an update or when the data changes on the server.
     * 
     * @param user - The new user data transfer object
     * @throws Error if the user parameter is null
     */
    public update(user: T) {
        if (user == null) {
            throw new Error('Base user cannot be null');
        }
        this._user = user;
    }

}
